/**
 * OnboardingWizard.tsx — ɳSentry new-user onboarding wizard
 *
 * Purpose:    Step-by-step wizard guiding new ɳSentry users through plugin
 *             installation and first monitor configuration.
 * Inputs:     None — wizard is self-contained
 * Outputs:    Multi-step wizard UI with step indicator and navigation
 * Constraints: Must not require auth — accessible before account creation
 */
'use client'

import { useState } from 'react'
import { Link } from 'react-router-dom'

const PLUGINS = [
  'nself-uptime-monitor',
  'nself-status-page',
  'nself-incident-mgmt',
  'nself-alert-router',
  'nself-slo-tracker',
  'nself-synthetic-monitor',
  'nself-rum',
  'nself-errors',
  'nself-cron-monitor',
  'nself-oncall',
  'nself-crash',
  'nself-anomaly',
  'nself-audit',
]

interface OnboardingWizardProps {
  step: number
}

export function OnboardingWizard({ step }: OnboardingWizardProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(PLUGINS))
  const [config, setConfig] = useState<Record<string, string>>({
    NSELF_UPTIME_WEBHOOK: '',
    NSELF_ALERT_EMAIL: '',
    NSELF_SLO_TARGET: '99.9',
  })

  const handleToggle = (plugin: string) => {
    const newSelected = new Set(selected)
    if (newSelected.has(plugin)) { newSelected.delete(plugin) } else { newSelected.add(plugin) }
    setSelected(newSelected)
  }

  const handleSelectAll = () => {
    setSelected(new Set(PLUGINS))
  }

  const handleClearAll = () => {
    setSelected(new Set())
  }

  const handleConfigChange = (key: string, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  const isNextDisabled = step === 1 && selected.size === 0
  const nextHref = step < 4 ? `/onboard/${step + 1}` : '/onboard/4'
  const prevHref = step > 1 ? `/onboard/${step - 1}` : null

  return (
    <div className="mx-auto max-w-3xl rounded-xl border border-brand-subtle bg-brand-surface p-8 shadow-lg">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-brand-ink">ɳSentry Onboarding</h1>
          <div className="rounded-full bg-sky-500/20 px-4 py-2 text-sm font-semibold text-sky-700 dark:text-sky-300">
            Step {step} of 4
          </div>
        </div>
        <div className="h-2 w-full rounded-full bg-brand-surface-2">
          <div
            className="h-2 rounded-full bg-sky-500 transition-all"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Step 1: Plugin Select */}
      {step === 1 && (
        <div>
          <h2 className="mb-2 text-2xl font-bold text-brand-ink">Select Plugins</h2>
          <p className="mb-6 text-brand-secondary">Choose which ɳSentry plugins to enable in your stack.</p>
          <div className="mb-6 flex gap-2">
            <button
              onClick={handleSelectAll}
              className="rounded-lg border border-brand-card px-4 py-2 text-sm text-brand-secondary transition hover:border-sky-500 hover:text-sky-700 dark:text-sky-400"
            >
              Select All
            </button>
            <button
              onClick={handleClearAll}
              className="rounded-lg border border-brand-card px-4 py-2 text-sm text-brand-secondary transition hover:border-gray-600 hover:text-gray-200"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-3">
            {PLUGINS.map(plugin => (
              <label key={plugin} className="flex cursor-pointer items-center gap-3 rounded-lg border border-brand-subtle p-4 transition hover:border-brand-card hover:bg-brand-surface-2/50">
                <input
                  type="checkbox"
                  checked={selected.has(plugin)}
                  onChange={() => handleToggle(plugin)}
                  className="h-4 w-4 rounded border-gray-600 bg-brand-surface-2 accent-sky-500"
                />
                <span className="font-medium text-gray-100">{plugin}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Configure */}
      {step === 2 && (
        <div>
          <h2 className="mb-2 text-2xl font-bold text-brand-ink">Configure Environment</h2>
          <p className="mb-6 text-brand-secondary">Set environment variables for your selected plugins.</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-200">Webhook URL</label>
              <input
                type="text"
                placeholder="https://your-app.com/webhooks/sentry"
                value={config.NSELF_UPTIME_WEBHOOK}
                onChange={e => handleConfigChange('NSELF_UPTIME_WEBHOOK', e.target.value)}
                className="mt-2 w-full rounded-lg border border-brand-card bg-brand-surface-2 px-4 py-2 text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">Alert Email</label>
              <input
                type="email"
                placeholder="ops@yourcompany.com"
                value={config.NSELF_ALERT_EMAIL}
                onChange={e => handleConfigChange('NSELF_ALERT_EMAIL', e.target.value)}
                className="mt-2 w-full rounded-lg border border-brand-card bg-brand-surface-2 px-4 py-2 text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200">SLO Target (%)</label>
              <input
                type="text"
                placeholder="99.9"
                value={config.NSELF_SLO_TARGET}
                onChange={e => handleConfigChange('NSELF_SLO_TARGET', e.target.value)}
                className="mt-2 w-full rounded-lg border border-brand-card bg-brand-surface-2 px-4 py-2 text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Test */}
      {step === 3 && (
        <div>
          <h2 className="mb-2 text-2xl font-bold text-brand-ink">Verify Configuration</h2>
          <p className="mb-6 text-brand-secondary">Run a diagnostic check to validate your setup.</p>
          <div className="space-y-4">
            <div className="rounded-lg border border-brand-card bg-brand-surface-2/50 p-4">
              <p className="mb-3 font-mono text-sm text-brand-secondary">Run this command in your nSelf server:</p>
              <div className="rounded-lg bg-brand-surface p-3 font-mono text-sm text-sky-700 dark:text-sky-300">
                nself doctor --deep --plugin nsentry
              </div>
            </div>
            <div className="rounded-lg border border-brand-card bg-brand-surface-2/50 p-4">
              <h3 className="mb-2 font-medium text-gray-200">Expected output:</h3>
              <div className="font-mono text-xs text-brand-muted">
                <div>✓ nself-uptime-monitor: OK</div>
                <div>✓ nself-status-page: OK</div>
                <div>✓ nself-incident-mgmt: OK</div>
                <div>... (all plugins verified)</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Deploy */}
      {step === 4 && (
        <div>
          <h2 className="mb-2 text-2xl font-bold text-brand-ink">Deploy & Verify</h2>
          <p className="mb-6 text-brand-secondary">Install plugins and deploy your ɳSentry stack.</p>
          <div className="space-y-4">
            <div className="rounded-lg border border-brand-card bg-brand-surface-2/50 p-4">
              <p className="mb-3 font-mono text-sm text-brand-secondary">Step 1: Install plugins</p>
              <div className="rounded-lg bg-brand-surface p-3 font-mono text-sm text-sky-700 dark:text-sky-300">
                nself plugin install nsentry
              </div>
            </div>
            <div className="rounded-lg border border-brand-card bg-brand-surface-2/50 p-4">
              <p className="mb-3 font-mono text-sm text-brand-secondary">Step 2: Build & deploy</p>
              <div className="rounded-lg bg-brand-surface p-3 font-mono text-sm text-sky-700 dark:text-sky-300">
                nself build && nself deploy
              </div>
            </div>
            <div className="rounded-lg border border-green-900/30 bg-green-50 dark:bg-green-900/10 p-4">
              <p className="text-sm text-green-700 dark:text-green-300">
                ✓ ɳSentry is now live on your instance. View your status page at <code className="font-mono text-xs">https://sentry.your-domain.com</code>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-8 flex gap-4">
        {prevHref ? (
          <Link
            to={prevHref}
            className="rounded-lg border border-brand-card px-6 py-2 font-semibold text-brand-secondary transition hover:border-gray-600 hover:text-brand-ink"
          >
            Back
          </Link>
        ) : (
          <button disabled className="rounded-lg border border-brand-subtle px-6 py-2 font-semibold text-brand-muted opacity-50">
            Back
          </button>
        )}
        <Link
          to={nextHref}
          className={`ml-auto rounded-lg px-6 py-2 font-semibold transition ${
            isNextDisabled
              ? 'border border-brand-card text-brand-muted opacity-50'
              : 'bg-sky-500 text-white hover:bg-sky-400'
          }`}
          style={{ pointerEvents: isNextDisabled ? 'none' : 'auto' }}
        >
          {step === 4 ? 'Complete' : 'Next'}
        </Link>
      </div>
    </div>
  )
}
